// Command Palette (like VS Code / Linear)
import { AppIcon, type IconName } from '@/components/ui/icons';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspaceAuthority } from '../contexts/WorkspaceAuthorityContext';
import { canCreateProjects, canCreateTasks, canInviteUsers } from '../lib/auth/capabilities';
import { useTheme } from '../contexts/ThemeContext';
import { resolveIdentityRoles } from '../lib/auth/identityRoles';
import { customerRouteHref, platformRouteHref } from '@/lib/navigation/canonicalRoutes';
import { getUserRouteAccess } from '../lib/auth/routeRegistry';
import { getEffectiveSurfaceCapabilities } from '@/lib/auth/effectiveSurfaceCapabilities';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface Command {
  id: string;
  title: string;
  subtitle?: string;
  icon: IconName;
  action: () => void;
  category: 'Navigation' | 'Actions' | 'Settings' | 'Theme';
  keywords?: string[];
  shortcut?: string;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, isOpen, { initialFocusSelector: 'input' });
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { workspaceId: validatedWorkspaceId, status: workspaceStatus } = useWorkspaceAuthority();
  const { platformRole } = resolveIdentityRoles(user);
  const caps = React.useMemo(() => getEffectiveSurfaceCapabilities(user), [user]);
  const { actualTheme, toggleTheme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scopedWorkspaceId = workspaceStatus === 'ready' ? validatedWorkspaceId : null;

  const customerLink = React.useCallback(
    (target: Parameters<typeof customerRouteHref>[1], options?: Parameters<typeof customerRouteHref>[2]) =>
      customerRouteHref(scopedWorkspaceId, target, options),
    [scopedWorkspaceId],
  );

  const customerNavTarget = React.useCallback((target: 'dashboard' | 'projects' | 'tasks' | 'users' | 'security' | 'settings') => {
    if (scopedWorkspaceId) {
      return customerLink(target);
    }
    switch (target) {
      case 'dashboard':
        return '/dashboard';
      case 'projects':
        return '/projects';
      case 'tasks':
        return '/tasks';
      case 'users':
        return '/users';
      case 'security':
        return '/security';
      case 'settings':
        return '/settings';
    }
  }, [customerLink, scopedWorkspaceId]);

  const commands = useMemo((): Command[] => [
    // Navigation
    ...(platformRole ? ([{
      id: 'nav-platform',
      title: 'Go to Control Center',
      subtitle: 'View platform operations and posture',
      icon: "layoutDashboard" as IconName,
      action: () => navigate(platformRouteHref('/platform')),
      category: 'Navigation' as const,
      keywords: ['platform', 'control', 'operations'],
      shortcut: 'Ctrl+0'
    }] as Command[]) : []),
    ...([{
      id: 'nav-dashboard',
      title: 'Go to Dashboard',
      subtitle: 'View project overview and metrics',
      icon: "layoutDashboard" as IconName,
      action: () => navigate(customerNavTarget('dashboard')),
      category: 'Navigation' as const,
      keywords: ['home', 'overview', 'metrics'],
      shortcut: 'Ctrl+1'
    }, {
      id: 'nav-projects',
      title: 'Go to Projects',
      subtitle: 'Manage all your projects',
      icon: "project" as IconName,
      action: () => navigate(customerNavTarget('projects')),
      category: 'Navigation' as const,
      keywords: ['portfolio'],
      shortcut: 'Ctrl+2'
    }, {
      id: 'nav-tasks',
      title: 'Go to Tasks',
      subtitle: 'View and manage tasks',
      icon: "task" as IconName,
      action: () => navigate(customerNavTarget('tasks')),
      category: 'Navigation' as const,
      keywords: ['todo', 'work'],
      shortcut: 'Ctrl+3'
    }, {
      id: 'nav-users',
      title: 'Go to Users',
      subtitle: 'Manage workspace users and collaborators',
      icon: "team" as IconName,
      action: () => navigate(customerNavTarget('users')),
      category: 'Navigation' as const,
      keywords: ['team', 'people', 'collaborators'],
      shortcut: 'Ctrl+4'
    }, {
      id: 'nav-security',
      title: 'Go to Security',
      subtitle: 'Review workspace security posture',
      icon: "security" as IconName,
      action: () => navigate(customerNavTarget('security')),
      category: 'Navigation' as const,
      keywords: ['privacy', 'audit', 'controls'],
      shortcut: 'Ctrl+5'
    }, {
      id: 'nav-settings',
      title: 'Open Settings',
      subtitle: 'Configure workspace settings',
      icon: "settings" as IconName,
      action: () => navigate(customerNavTarget('settings')),
      category: 'Navigation' as const,
      keywords: ['preferences', 'configuration'],
      shortcut: 'Ctrl+,'
    }].filter((command) => user ? getUserRouteAccess(user, customerNavTarget(
      command.id === 'nav-dashboard' ? 'dashboard'
        : command.id === 'nav-projects' ? 'projects'
        : command.id === 'nav-tasks' ? 'tasks'
        : command.id === 'nav-users' ? 'users'
        : command.id === 'nav-security' ? 'security'
        : 'settings',
    )).allowed : false) as Command[]),

    // Actions
    ...(scopedWorkspaceId && canCreateProjects(caps) ? ([{
      id: 'action-new-project',
      title: 'Create New Project',
      subtitle: 'Start a new project with tasks and milestones',
      icon: "project" as IconName,
      action: () => navigate(customerLink('projects', { search: { action: 'create' } })),
      category: 'Actions' as const,
      keywords: ['new', 'create'],
      shortcut: 'Ctrl+Shift+P'
    }] as Command[]) : []),

    ...(scopedWorkspaceId && canCreateTasks(caps) ? ([{
      id: 'action-new-task',
      title: 'Create New Task',
      subtitle: 'Add a task to any project',
      icon: "task" as IconName,
      action: () => navigate(customerLink('tasks', { search: { action: 'create' } })),
      category: 'Actions' as const,
      keywords: ['new', 'create', 'todo'],
      shortcut: 'Ctrl+Shift+T'
    }] as Command[]) : []),

    ...(scopedWorkspaceId && canInviteUsers(caps) ? ([{
      id: 'action-invite-user',
      title: 'Invite User',
      subtitle: 'Send an invitation to collaborate',
      icon: "team" as IconName,
      action: () => navigate(customerLink('users', { search: { action: 'invite' } })),
      category: 'Actions' as const,
      keywords: ['invite', 'team', 'collaborate'],
      shortcut: 'Ctrl+Shift+I'
    }] as Command[]) : []),

    // Settings
    ...(scopedWorkspaceId ? ([{
      id: 'settings-preferences',
      title: 'Open Settings',
      subtitle: 'Configure your preferences',
      icon: "settings" as IconName,
      action: () => navigate(customerNavTarget('settings')),
      category: 'Settings',
      keywords: ['preferences', 'config'],
      shortcut: 'Ctrl+,'
    },
    {
      id: 'theme-toggle',
      title: `Switch to ${actualTheme === 'light' ? 'Dark' : 'Light'} Mode`,
      subtitle: `Currently using ${actualTheme} mode`,
      icon: (actualTheme === 'light' ? "moon" : "sun") as IconName,
      action: toggleTheme,
      category: 'Theme',
      keywords: ['dark', 'light', 'appearance'],
      shortcut: 'Ctrl+Shift+L'
    },
    {
      id: 'auth-logout',
      title: 'Sign Out',
      subtitle: `Signed in as ${user?.name}`,
      icon: "logOut" as IconName,
      action: logout,
      category: 'Settings',
      keywords: ['logout', 'exit', 'quit']
    }] as Command[]) : [])
  ], [platformRole, customerLink, customerNavTarget, navigate, caps, actualTheme, toggleTheme, user, logout, scopedWorkspaceId]);

  const filteredCommands = useMemo(() => {
    if (!searchTerm.trim()) return commands;

    const term = searchTerm.toLowerCase();
    return commands.filter(cmd =>
      cmd.title.toLowerCase().includes(term) ||
      cmd.subtitle?.toLowerCase().includes(term) ||
      cmd.keywords?.some(keyword => keyword.toLowerCase().includes(term))
    );
  }, [commands, searchTerm]);

  const groupedCommands = useMemo(() => {
    const groups: Record<string, Command[]> = {};
    filteredCommands.forEach(cmd => {
      if (!groups[cmd.category]) groups[cmd.category] = [];
      groups[cmd.category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredCommands.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            onClose();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onClose]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Clear search when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const getCategoryIcon = (category: string): IconName => {
    switch (category) {
      case 'Navigation': return "layoutDashboard";
      case 'Actions': return "folderOpen";
      case 'Settings': return "settings";
      case 'Theme': return actualTheme === 'light' ? "moon" : "sun";
      default: return "terminal";
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Shared scrim separates commands from the underlying work. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="ax-overlay-scrim ax-overlay-scrim--soft fixed inset-0 z-[70]"
          />

          {/* Layout owns centering; motion must not overwrite translateX. */}
          <div className="pointer-events-none fixed inset-x-0 top-20 bottom-4 z-[80] flex items-start justify-center px-4">
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="pointer-events-auto w-full max-w-2xl"
          >
            <div className="ax-overlay-panel ax-overlay-modal flex max-h-[calc(100dvh-6rem)] flex-col overflow-hidden">
              {/* Search Header */}
              <div className="ax-overlay-header shrink-0 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-surface-muted rounded-xl">
                    <AppIcon name="terminal" className="w-4 h-4 text-text-muted" />
                  </div>
                  <input
                    type="text"
                    aria-label="Search commands"
                    placeholder="Search commands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="min-w-0 flex-1 rounded-sm bg-transparent border-0 px-2 py-1 text-text-primary placeholder:text-text-muted text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                  />
                  <div className="text-xs text-text-muted font-mono bg-surface-muted px-2 py-1 rounded-lg">
                    Ctrl+K
                  </div>
                </div>
              </div>

              {/* Commands List */}
              <div className="min-h-0 max-h-96 overflow-y-auto">
                {Object.keys(groupedCommands).length > 0 ? (
                  <div className="p-2">
                    {Object.entries(groupedCommands).map(([category, cmds], groupIndex) => (
                      <div key={category} className={groupIndex > 0 ? 'mt-4' : ''}>
                        {/* Category Header */}
                        <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wide">
                          <AppIcon name={getCategoryIcon(category)} className="w-3 h-3" />
                          {category}
                        </div>

                        {/* Commands in Category */}
                        {cmds.map((cmd) => {
                          const globalIndex = filteredCommands.indexOf(cmd);
                          return (
                            <motion.button
                              key={cmd.id}
                              onClick={() => {
                                cmd.action();
                                onClose();
                              }}
                              className={`ax-action group w-full p-3 rounded-lg text-left ${globalIndex === selectedIndex
                                ? 'bg-surface-selected border border-accent-primary/30'
                                : 'hover:bg-surface-muted/50 border border-transparent'
                                }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-xl ${globalIndex === selectedIndex
                                  ? 'bg-accent-primary/10'
                                  : 'bg-surface-muted'
                                  }`}>
                                  <AppIcon name={cmd.icon} className={`w-4 h-4 ${globalIndex === selectedIndex
                                    ? 'text-accent-primary'
                                    : 'text-text-muted'
                                    }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-medium text-text-primary truncate">
                                      {cmd.title}
                                    </h4>
                                    {cmd.shortcut && (
                                      <span className="text-xs text-text-muted font-mono bg-surface-muted px-2 py-1 rounded-lg ml-2">
                                        {cmd.shortcut}
                                      </span>
                                    )}
                                  </div>
                                  {cmd.subtitle && (
                                    <p className="text-sm text-text-muted truncate mt-1">
                                      {cmd.subtitle}
                                    </p>
                                  )}
                                </div>
                                <AppIcon name="arrowRight" className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <AppIcon name="search" className="w-8 h-8 text-text-on-muted mx-auto mb-3" />
                    <p className="text-text-muted">No commands found</p>
                    <p className="text-sm text-text-muted mt-1">Try different keywords</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="ax-overlay-footer shrink-0 p-3 bg-surface-muted/50">
                <div className="flex items-center justify-between text-xs text-text-muted">
                  <div className="flex items-center gap-4">
                    <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">↑↓</kbd> Navigate</span>
                    <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">⏎</kbd> Execute</span>
                  </div>
                  <span><kbd className="px-1.5 py-0.5 bg-surface-chip rounded-sm">Esc</kbd> Close</span>
                </div>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
